import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLang } from "../LanguageContext";
import { Reveal, Eyebrow } from "./Reveal";

const initials = (name) => name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

export const Testimonials = () => {
  const { t } = useLang();
  const items = t.testimonials.items;
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);

  const paginate = useCallback((d) => {
    setDir(d);
    setIdx((p) => (p + d + items.length) % items.length);
  }, [items.length]);

  useEffect(() => {
    const timer = setInterval(() => paginate(1), 6000);
    return () => clearInterval(timer);
  }, [paginate]);

  const item = items[idx];

  return (
    <section data-testid="testimonials" className="relative py-24 md:py-32 bg-[#050505]">
      <div className="max-w-[1100px] mx-auto px-5 md:px-10">
        <Reveal><Eyebrow testId="testimonials-eyebrow">{t.testimonials.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display text-4xl md:text-6xl text-white tracking-tight leading-[1.05] mb-14">
            {t.testimonials.title1} <span className="text-gold-gradient italic">{t.testimonials.title2}</span>
          </h2>
        </Reveal>

        <div className="glass rounded-3xl p-8 md:p-14 relative overflow-hidden min-h-[320px] md:min-h-[300px]">
          <Quote className="absolute top-8 right-8 text-[#d4af37]/15" size={80} />
          <AnimatePresence mode="wait" custom={dir}>
            <motion.div
              key={idx}
              custom={dir}
              initial={{ opacity: 0, x: dir * 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: dir * -40 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`testimonial-${idx}`}
            >
              <p className="font-display text-2xl md:text-4xl text-white leading-snug tracking-tight max-w-3xl">
                &ldquo;{item.quote}&rdquo;
              </p>
              <div className="mt-10 flex items-center gap-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center border border-[#d4af37]/40 bg-[#d4af37]/10 text-[#d4af37] font-display text-lg">
                  {initials(item.name)}
                </div>
                <div>
                  <div className="text-white font-medium">{item.name}</div>
                  <div className="text-sm text-[#a3a3a3]">{item.role}</div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-between mt-8">
          <div className="flex gap-2">
            {items.map((_, i) => (
              <button
                key={i}
                data-testid={`testimonial-dot-${i}`}
                onClick={() => { setDir(i > idx ? 1 : -1); setIdx(i); }}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-[#d4af37]" : "w-1.5 bg-white/20"}`}
                aria-label={`Testimonial ${i + 1}`}
              />
            ))}
          </div>
          <div className="flex gap-3">
            <button data-testid="testimonial-prev" onClick={() => paginate(-1)} className="w-11 h-11 rounded-full glass flex items-center justify-center text-white hover:text-[#d4af37] hover:border-[#d4af37]/40 transition-colors duration-300">
              <ChevronLeft size={20} />
            </button>
            <button data-testid="testimonial-next" onClick={() => paginate(1)} className="w-11 h-11 rounded-full glass flex items-center justify-center text-white hover:text-[#d4af37] hover:border-[#d4af37]/40 transition-colors duration-300">
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
